/**
 * The stored form of a mention: `@[username](user:id)`.
 *
 * The API owns the canonical version of this — it rewrites labels on write, so
 * what arrives here is already trustworthy. This copy exists because the web
 * project never imports from `api/` (spec §12), and the two are pinned together
 * by the e2e tests either side of the wire.
 */
const TOKEN = /@\[([^\]\n]{1,60})\]\((user|business):([A-Za-z0-9_-]{1,40})\)/g;

export const MAX_MENTIONS_PER_POST = 5;
export const MAX_MENTIONS_PER_COMMENT = 3;

export type MentionKind = 'user' | 'business';

export interface MentionSegment {
  kind: 'mention';
  label: string;
  targetKind: MentionKind;
  targetId: string;
}

export interface TextSegment {
  kind: 'text';
  text: string;
}

export type BodySegment = TextSegment | MentionSegment;

/**
 * Splits a body into plain text and mentions.
 *
 * Segments rather than HTML: the renderer builds React elements from these, so
 * user text stays escaped by React and there is no `dangerouslySetInnerHTML`
 * anywhere near a post (spec §11).
 */
export function splitMentions(body: string): BodySegment[] {
  const segments: BodySegment[] = [];
  let cursor = 0;

  for (const match of body.matchAll(TOKEN)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ kind: 'text', text: body.slice(cursor, start) });
    }

    const [raw, label = '', targetKind = 'user', targetId = ''] = match;
    segments.push({
      kind: 'mention',
      label,
      targetKind: targetKind === 'business' ? 'business' : 'user',
      targetId,
    });
    cursor = start + raw.length;
  }

  if (cursor < body.length) {
    segments.push({ kind: 'text', text: body.slice(cursor) });
  }

  return segments;
}

/** How many distinct accounts a body tags — for the composer's counter. */
export function countMentions(body: string): number {
  const seen = new Set<string>();
  for (const match of body.matchAll(TOKEN)) {
    seen.add(`${match[2]}:${match[3]}`);
  }
  return seen.size;
}

export interface MentionRef {
  label: string;
  targetKind: MentionKind;
  targetId: string;
}

/**
 * What the writer sees: `@[Alina Berce](user:cuid)` becomes `@Alina Berce`.
 *
 * The stored form is an implementation detail of the link. Showing it in the
 * composer means showing somebody a database id in the middle of their own
 * sentence, and then making them edit around it.
 */
export function toDisplayText(markup: string): string {
  return markup.replace(TOKEN, (_raw, label: string) => `@${label}`);
}

/** The mentions a stored body refers to, in the order they appear. */
export function mentionRefs(markup: string): MentionRef[] {
  return [...markup.matchAll(TOKEN)].map((match) => ({
    label: match[1] ?? '',
    targetKind: match[2] === 'business' ? 'business' : 'user',
    targetId: match[3] ?? '',
  }));
}

/**
 * Turns what the writer typed back into what gets stored.
 *
 * Each `@Name` is matched against the mentions picked in this session, longest
 * label first so "@Ana Maria" is not consumed by an earlier "@Ana". A name that
 * has been edited since it was picked no longer matches, and stays plain text —
 * which is the honest outcome: the writer changed it, so it is no longer a tag.
 */
export function toMarkup(display: string, refs: readonly MentionRef[]): string {
  const byLength = [...refs].sort((a, b) => b.label.length - a.label.length);
  let result = '';
  let cursor = 0;

  outer: while (cursor < display.length) {
    if (display[cursor] === '@') {
      for (const ref of byLength) {
        if (display.startsWith(`@${ref.label}`, cursor)) {
          result += `@[${ref.label}](${ref.targetKind}:${ref.targetId})`;
          cursor += ref.label.length + 1;
          continue outer;
        }
      }
    }

    result += display[cursor];
    cursor += 1;
  }

  return result;
}

export interface ActiveQuery {
  /** What has been typed after the `@`, so far. */
  query: string;
  /** Where the `@` sits, for replacing the whole thing on selection. */
  start: number;
  end: number;
}

/**
 * The `@…` being typed at the caret, if there is one.
 *
 * Only fires at a word boundary, so an email address never opens the picker —
 * "write to alina@example" should not offer to tag anybody.
 */
export function activeMentionQuery(value: string, caret: number): ActiveQuery | null {
  const upToCaret = value.slice(0, caret);
  const at = upToCaret.lastIndexOf('@');
  if (at === -1) return null;

  const before = at === 0 ? '' : upToCaret.charAt(at - 1);
  if (before && !/\s|[([]/.test(before)) return null;

  const query = upToCaret.slice(at + 1);
  // A space ends it, and so does the closing bracket of a token already placed.
  if (/[\s\]()]/.test(query)) return null;
  if (query.length > 40) return null;

  return { query, start: at, end: caret };
}

/** Replaces the `@…` under the caret with the person's name. */
export function insertMention(
  display: string,
  active: ActiveQuery,
  mention: { username: string; displayName: string; type: string; id: string },
): { display: string; caret: number; ref: MentionRef } {
  const targetKind: MentionKind = mention.type === 'BUSINESS' ? 'business' : 'user';
  const label = mention.displayName || mention.username;
  const written = `@${label} `;

  return {
    display: `${display.slice(0, active.start)}${written}${display.slice(active.end)}`,
    caret: active.start + written.length,
    ref: { label, targetKind, targetId: mention.id },
  };
}
